/**
 * Chip — the one chip/pill/badge. Replaces nine hand-rolled variants
 * (Brief 5 §B): ContentBadge, MarketBadge, RankChip, ScorePill, form
 * chips, metadata pills, 18+/RG chrome, kickers, status dots-with-text.
 *
 * `tone` maps 1:1 onto the colour-source rule (widget-color-sources.md):
 *   status  → --status-* / --form-*   (outcomes: W/D/L, live, settled)
 *   brand   → --accent-* / --bg-insight (rank chips, market badges, kickers)
 *   neutral → text ramp + --border     (metadata, 18+/RG — NEVER accent)
 *
 * ENFORCED BY OMISSION: there is no colour prop. A chip can never carry
 * a club colour (colour-source rule: club colours live in crests, heroes
 * and flags only). If a call-site needs a colour that tone+state can't
 * express, the colour-source table is wrong or the call-site is — fix
 * that, don't extend this.
 *
 * ScorePill becomes `<Chip tone="status" state={...}>1–1</Chip>`.
 */

import type { ReactElement, ReactNode } from 'react';
import styles from './Chip.module.css';

export type ChipTone = 'status' | 'brand' | 'neutral';
/** Colour picker WITHIN the status tone (maps to --form-* / --status-*). */
export type ChipState = 'win' | 'draw' | 'loss' | 'positive' | 'negative' | 'warning' | 'neutral';
export type ChipSize = 'sm' | 'md';
export type ChipShape = 'chip' | 'pill';
export type ChipVariant = 'filled' | 'outline';

export interface ChipProps {
  tone: ChipTone;
  /** Only meaningful for tone="status"; ignored otherwise. */
  state?: ChipState;
  size?: ChipSize;
  /** chip → --radius-chip; pill → --radius-pill (fully round). */
  shape?: ChipShape;
  variant?: ChipVariant;
  /** Leading glyph slot (icon, dot, emoji). */
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function Chip({
  tone,
  state = 'neutral',
  size = 'md',
  shape = 'chip',
  variant = 'filled',
  icon,
  children,
  className,
  'data-testid': testId,
}: ChipProps): ReactElement {
  const classes = [
    styles.chip,
    styles[`tone-${tone}`],
    tone === 'status' ? styles[`state-${state}`] : '',
    styles[`size-${size}`],
    styles[`shape-${shape}`],
    styles[`variant-${variant}`],
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} data-testid={testId}>
      {icon != null ? <span className={styles.icon}>{icon}</span> : null}
      {children}
    </span>
  );
}
